import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Message } from "rsuite";
import PageWrap from "../common/PageWrap";
import styles from "./RideOrderPage.module.scss";
import MapWrapper from "../ride-order/MapWrapper";
import OrderForm from "../ride-order/form/OrderForm";
import ElitVehiclePanel from "../ride-order/form/ElitVehiclePanel";
import { applyElitVehicleId } from "../ride-order/elitVehicleSelection";
import { createInitialFormState } from "../ride-order/orderFormConfig";
import { hydrateStoredPassenger } from "../ride-order/hydrateStoredPassenger";
import { DEFAULT_ORDER_FORM_LOCALE } from "../ride-order/orderFormLocale";
import { scrollToMapIfMobile } from "../ride-order/orderFormConstants";
import {
  buildRideOrderRequestPayload,
  getRideOrderApiErrorMessage,
} from "../ride-order/rideOrderPayload";
import { ROUTES } from "../../constants/routes";
import { useGlobalContext } from "../../GlobalContext";
import { isSpecialCompany } from "../../types/company";
import NetworkService from "../../NetworkService";
import { toasterFunction } from "../utils";
import { parseFavoriteAddressesResponse } from "../ride-order/favoriteAddresses";
import { FavoriteAddress, OrderFormMapState, OrderFormState, RideType, RouteCoords, isScheduleRide } from "../ride-order/types";

const EMPTY_MAP_STATE: OrderFormMapState = { markers: [] };

function parseRideType(searchParams: URLSearchParams): RideType {
  return searchParams.get("type") === "schedule" ? "schedule" : "now";
}

function RideOrderPage() {
  const { user: userState, companyData: companyDataState } = useGlobalContext();
  const [admin] = userState;
  const [companyData] = companyDataState;
  const companyAdminId = admin?.user?.id;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rideType = parseRideType(searchParams);

  const mapLayerRef = useRef<HTMLDivElement>(null);
  const skipFormResetRef = useRef(false);
  const getMapCenterRef = useRef<(() => RouteCoords) | null>(null);

  const [form, setForm] = useState<OrderFormState>(() => createInitialFormState(rideType));
  const [mapState, setMapState] = useState<OrderFormMapState>(EMPTY_MAP_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isElitVehiclePanelOpen, setIsElitVehiclePanelOpen] = useState(false);
  const [favoriteAddresses, setFavoriteAddresses] = useState<FavoriteAddress[]>([]);

  const authToken = admin?.authorisation?.token;

  useEffect(() => {
    if (!companyData) return;
    if (!isSpecialCompany(companyData)) {
      navigate(ROUTES.ROOT, { replace: true });
    }
  }, [companyData, navigate]);

  useEffect(() => {
    if (!authToken || !NetworkService.jwtToken) return;
    void hydrateStoredPassenger(setForm);
  }, [authToken]);

  useEffect(() => {
    if (!authToken || !NetworkService.jwtToken) return;

    NetworkService.getFavoriteAddresses()
      .then((response) => {
        setFavoriteAddresses(parseFavoriteAddressesResponse(response));
      })
      .catch(() => {
        setFavoriteAddresses([]);
      });
  }, [authToken]);

  useEffect(() => {
    if (skipFormResetRef.current) {
      skipFormResetRef.current = false;
      return;
    }
    setForm(createInitialFormState(parseRideType(searchParams)));
    setMapState(EMPTY_MAP_STATE);
    setIsElitVehiclePanelOpen(false);
    if (!authToken || !NetworkService.jwtToken) return;
    void hydrateStoredPassenger(setForm);
  }, [authToken, searchParams]);

  useEffect(() => {
    if (!isScheduleRide(rideType)) {
      setIsElitVehiclePanelOpen(false);
    }
  }, [rideType]);

  const handleFormChange = useCallback(
    (updates: Partial<OrderFormState> | ((prev: OrderFormState) => OrderFormState)) => {
      setForm((prev) => (typeof updates === "function" ? updates(prev) : { ...prev, ...updates }));
    },
    []
  );

  const handleMapStateChange = useCallback((state: OrderFormMapState) => {
    setMapState(state);
  }, []);

  const handleMapCenterGetterReady = useCallback((getCenter: () => RouteCoords) => {
    getMapCenterRef.current = getCenter;
  }, []);

  const getMapCenter = useCallback((): RouteCoords => {
    return getMapCenterRef.current?.() ?? { lat: 44.81631398468087, lng: 20.460408134616113 };
  }, []);

  const handleScrollToMap = useCallback(() => {
    scrollToMapIfMobile(mapLayerRef.current);
  }, []);

  const handleRideTypeChange = useCallback(
    (nextRideType: RideType) => {
      if (nextRideType === rideType) return;

      skipFormResetRef.current = true;
      setForm(createInitialFormState(nextRideType));
      setMapState(EMPTY_MAP_STATE);
      setIsElitVehiclePanelOpen(false);
      if (authToken && NetworkService.jwtToken) {
        void hydrateStoredPassenger(setForm);
      }

      const nextParams = new URLSearchParams();
      nextParams.set("type", nextRideType);
      setSearchParams(nextParams, { replace: true });
    },
    [authToken, rideType, setSearchParams]
  );

  const handleElitVehicleChange = useCallback(
    (vehicleId: string) => {
      handleFormChange((prev) => applyElitVehicleId(prev, vehicleId));
    },
    [handleFormChange]
  );

  const handleSubmit = useCallback(
    async (formData: OrderFormState) => {
      if (!companyAdminId) {
        throw new Error("Missing company admin session");
      }

      const payload = buildRideOrderRequestPayload(formData, rideType);
      if (!payload) {
        throw new Error("Please complete all required fields");
      }

      setIsLoading(true);
      try {
        const response = await NetworkService.createCompanyRideRequest(payload, companyAdminId);
        if (!response?.success) {
          throw { data: { message: response?.message ?? "Booking request failed" } };
        }

        const rideId = response?.ride_id;
        const baseMessage = isScheduleRide(rideType)
          ? "Ride successfully scheduled."
          : "Ride successfully ordered!";

        toasterFunction(
          <Message full closable showIcon type="success">
            {rideId ? (
              <>
                {baseMessage}
                <br />
                Service ID: {rideId}
              </>
            ) : (
              baseMessage
            )}
          </Message>
        );

        setIsLoading(false);
        navigate(`${ROUTES.ROOT}#${ROUTES.RECENT_SERVICES_HASH}`);
      } catch (error) {
        const message = getRideOrderApiErrorMessage(error);
        toasterFunction(
          <Message full closable showIcon type="error">
            {message}
          </Message>
        );
        throw error instanceof Error ? error : new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [companyAdminId, navigate, rideType]
  );

  const locale = {
    ...DEFAULT_ORDER_FORM_LOCALE,
    elit: { ...DEFAULT_ORDER_FORM_LOCALE.elit },
  };

  if (companyData && !isSpecialCompany(companyData)) {
    return null;
  }

  return (
    <PageWrap hideBreadcrumbs>
      <div className={styles.rideOrderPage}>
        <div className={styles.mapLayer} ref={mapLayerRef}>
          <MapWrapper
            markers={mapState.markers}
            onMarkerDrag={mapState.onMarkerDrag}
            onMapCenterGetterReady={handleMapCenterGetterReady}
          />
        </div>

        <div className={styles.formCard}>
          <OrderForm
            locale={locale}
            form={form}
            favoriteAddresses={favoriteAddresses}
            companyAdminId={companyAdminId}
            onFormChange={handleFormChange}
            onMapStateChange={handleMapStateChange}
            onScrollToMap={handleScrollToMap}
            getMapCenter={getMapCenter}
            onSubmit={handleSubmit}
            isSubmitting={isLoading}
            isElitVehiclePanelOpen={isElitVehiclePanelOpen}
            onElitVehiclePanelOpenChange={setIsElitVehiclePanelOpen}
            rideType={rideType}
            onRideTypeChange={handleRideTypeChange}
          />
        </div>

        {isScheduleRide(rideType) && (
          <ElitVehiclePanel
            isOpen={isElitVehiclePanelOpen}
            onClose={() => setIsElitVehiclePanelOpen(false)}
            vehicleId={form.vehicleId}
            locale={locale}
            onVehicleChange={handleElitVehicleChange}
            onConfirm={() => setIsElitVehiclePanelOpen(false)}
          />
        )}

      </div>
    </PageWrap>
  );
}

export default RideOrderPage;
